import { useState } from 'react';
import { PermissionList } from './PermissionList';
import { PermissionForm } from './PermissionForm';
import { aclAPI, ACLPermission } from '../../api/aclApi';
import { Button } from '@/components/ui/button';
import { Plus, Download, Copy } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

function downloadJson(filename: string, data: unknown) {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export const PermissionManager = () => {
    const [showForm, setShowForm] = useState(false);
    const [editingPermission, setEditingPermission] = useState<ACLPermission | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [exporting, setExporting] = useState(false);
    const [copyingJson, setCopyingJson] = useState(false);
    const [copyLabel, setCopyLabel] = useState('Copy as JSON');

    const handleExportJson = async () => {
        try {
            setExporting(true);
            const { data: permissions } = await aclAPI.getAllPermissions();
            const payload = {
                exportedAt: new Date().toISOString(),
                count: permissions.length,
                permissions,
            };
            const filename = `acl-permissions-${new Date().toISOString().slice(0, 10)}.json`;
            downloadJson(filename, payload);
        } catch (err) {
            console.error('Export ACLs failed:', err);
        } finally {
            setExporting(false);
        }
    };
    const handleCopyJson = async () => {
        try {
            setCopyingJson(true);
            const { data: permissions } = await aclAPI.getAllPermissions();
            const payload = {
                exportedAt: new Date().toISOString(),
                count: permissions.length,
                permissions,
            };
            await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
            setCopyLabel('Copied');
            window.setTimeout(() => setCopyLabel('Copy as JSON'), 1800);
        } catch (err) {
            console.error('Copy ACL JSON failed:', err);
            setCopyLabel('Copy failed');
            window.setTimeout(() => setCopyLabel('Copy as JSON'), 2200);
        } finally {
            setCopyingJson(false);
        }
    };

    const handleEdit = (permission: ACLPermission) => {
        setEditingPermission(permission);
        setShowForm(true);
    };

    const handleNew = () => {
        setEditingPermission(null);
        setShowForm(true);
    };

    const handleSave = () => {
        setShowForm(false);
        setEditingPermission(null);
        setRefreshKey(prev => prev + 1); // Trigger refresh
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingPermission(null);
    };

    return (
        <div className="container mx-auto h-full max-w-6xl overflow-y-auto p-6">
            <div className="mb-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold">ACL Permission Manager</h1>
                        <p className="text-gray-600 mt-1">
                            Manage access control permissions for users, groups, and roles
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={handleCopyJson}
                            disabled={copyingJson}
                        >
                            <Copy className="w-4 h-4 mr-2" />
                            {copyingJson ? 'Copying…' : copyLabel}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleExportJson}
                            disabled={exporting}
                        >
                            <Download className="w-4 h-4 mr-2" />
                            {exporting ? 'Exporting…' : 'Export as JSON'}
                        </Button>
                        <Button onClick={handleNew}>
                            <Plus className="w-4 h-4 mr-2" />
                            New Permission
                        </Button>
                    </div>
                </div>
            </div>

            <Alert className="mb-4">
                <AlertTitle>ACL audience model</AlertTitle>
                <AlertDescription>
                    Use <code>public_*</code> actions for unauthenticated endpoint/payload exposure and <code>all_users_*</code> for authenticated global access.
                    <br />
                    Full-collection actions publish/access entire payloads, while <code>*_expose_fields</code> actions allow selective field exposure.
                </AlertDescription>
            </Alert>

            <PermissionList
                refreshKey={refreshKey}
                onEdit={handleEdit}
                onRefresh={() => setRefreshKey(prev => prev + 1)}
            />

            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingPermission ? 'Edit Permission' : 'Create New Permission'}
                        </DialogTitle>
                    </DialogHeader>
                    <PermissionForm
                        permission={editingPermission}
                        onSave={handleSave}
                        onCancel={handleCancel}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PermissionManager;
